// ===========================================
// BordereauProduction — Page rapport « Bordereau de production »
// ===========================================
import React, { useState, useEffect, useMemo } from 'react';
import {
    Card, Table, Select, Button, DatePicker, Grid, Tag, Dropdown, Empty, Switch, Modal,
} from 'antd';
import {
    ProfileOutlined, DownloadOutlined, FilePdfOutlined, FileExcelOutlined,
    ClearOutlined, FileTextOutlined, DollarOutlined, BankOutlined, GiftOutlined,
    QuestionCircleOutlined, CloseOutlined,
} from '@ant-design/icons';
import { bilansService, agencesService, compagniesService, typesAssuranceService } from '../../common/services';
import { useGlobalAgenceState, useCanSwitchAgence, useUserAgenceId, useExporterName } from '../../common/hooks';
import { resolveAgenceLabel, addExporterFooter } from '../../common/utils/pdfExport';
import { toast } from 'react-toastify';

// Colonnes monétaires (1-based) du bordereau : montants alignés à droite
const BP_MONEY_COLS = new Set([14, 15, 16, 17, 18, 19, 20, 22]);
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatMontant, formatDate as formatDateShared } from '../../common/utils/format';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './bordereau-production.page.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

const fmt = (v) => formatMontant(v);
const fmtPct = (v) => v ? `${v}%` : '';
const fmtDate = (d) => formatDateShared(d, { fallback: '' });

const periodeLabels = {
    mensuel: '1 Mois', trimestriel: '3 Mois', semestriel: '6 Mois', annuel: '12 Mois',
};

const BordereauProduction = () => {
    const [data, setData] = useState([]);
    const [guideOpen, setGuideOpen] = useState(false);
    const [totaux, setTotaux] = useState({});
    const [loading, setLoading] = useState(false);
    const [agences, setAgences] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [typesAssurance, setTypesAssurance] = useState([]);
    const [selectedAgence, setSelectedAgence] = useGlobalAgenceState();
    const canSwitchAgence = useCanSwitchAgence();
    const userAgenceId = useUserAgenceId();
    const exporterName = useExporterName();
    const [selectedCompagnie, setSelectedCompagnie] = useState('all');
    const [selectedType, setSelectedType] = useState('all');
    const [dateRange, setDateRange] = useState(null);
    const [showOperateur, setShowOperateur] = useState(false);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    useEffect(() => {
        Promise.all([
            agencesService.getAll().catch(() => []),
            compagniesService.getAll().catch(() => []),
            typesAssuranceService.getAll().catch(() => []),
        ]).then(([agRes, compRes, typeRes]) => {
            setAgences(Array.isArray(agRes) ? agRes : agRes?.data || []);
            setCompagnies(Array.isArray(compRes) ? compRes : compRes?.data || []);
            setTypesAssurance(Array.isArray(typeRes) ? typeRes : typeRes?.data || []);
        });
    }, []);

    const fetchData = async () => {
        const params = {};
        if (selectedAgence !== 'all') params.agence = selectedAgence;
        if (selectedCompagnie !== 'all') params.compagnie = selectedCompagnie;
        if (selectedType !== 'all') params.typeAssurance = selectedType;
        if (dateRange && dateRange[0] && dateRange[1]) {
            params.start = dateRange[0].format('YYYY-MM-DD');
            params.end = dateRange[1].format('YYYY-MM-DD');
        }

        setLoading(true);
        try {
            const res = await bilansService.bordereauProduction(params);
            const result = res?.data || res || {};
            setData(result.data || []);
            setTotaux(result.totaux || {});
        } catch (error) {
            console.error('Erreur chargement bordereau production:', error);
            setData([]);
            setTotaux({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [selectedAgence, selectedCompagnie, selectedType, dateRange]);

    const activeFiltersCount = useMemo(() => {
        let n = 0;
        if (selectedAgence !== 'all') n++;
        if (selectedCompagnie !== 'all') n++;
        if (selectedType !== 'all') n++;
        if (dateRange) n++;
        return n;
    }, [selectedAgence, selectedCompagnie, selectedType, dateRange]);

    const clearAllFilters = () => {
        setSelectedAgence('all');
        setSelectedCompagnie('all');
        setSelectedType('all');
        setDateRange(null);
    };

    const allColumns = [
        { title: 'N°', dataIndex: 'numero', key: 'numero', width: 60, fixed: isMobile ? false : 'left', align: 'center' },
        {
            title: 'Type assurance',
            dataIndex: 'typeAssurance',
            key: 'typeAssurance',
            width: 190,
            ellipsis: true,
            render: (t) => <Tag color="geekblue" className="bp-tag">{t}</Tag>,
        },
        { title: 'N° Attestation', dataIndex: 'numeroAttestation', key: 'attestation', width: 130, ellipsis: true },
        { title: 'N° CEDEAO', dataIndex: 'numeroCedeao', key: 'cedeao_num', width: 120, ellipsis: true },
        {
            title: 'N° Police',
            dataIndex: 'numeroPolice',
            key: 'police',
            width: 170,
            ellipsis: true,
            render: (t) => <span className="bp-police">{t}</span>,
        },
        { title: 'Matricule', dataIndex: 'immatriculation', key: 'matricule', width: 120, ellipsis: true },
        { title: 'Marque', dataIndex: 'marqueVehicule', key: 'marque', width: 130, ellipsis: true },
        { title: 'Catégorie', dataIndex: 'categorieVehicule', key: 'categorie', width: 110, ellipsis: true },
        {
            title: 'Type client',
            dataIndex: 'typeClient',
            key: 'typeClient',
            width: 120,
            render: (t) => (
                <Tag color={t === 'entreprise' ? 'orange' : 'blue'} className="bp-tag">
                    {(t || '').toUpperCase()}
                </Tag>
            ),
        },
        { title: 'Assuré', dataIndex: 'assure', key: 'assure', width: 170, ellipsis: true },
        { title: "Date d'effet", dataIndex: 'dateEffet', key: 'dateEffet', width: 115, render: fmtDate },
        { title: 'Période', key: 'periodes', width: 90, render: (_, r) => periodeLabels[r.periode] || r.periode || (r.duree ? `${r.duree} Mois` : '') },
        { title: "Date d'échéance", dataIndex: 'dateEcheance', key: 'dateEcheance', width: 125, render: fmtDate },
        { title: 'Prime nette', dataIndex: 'primeNette', key: 'primeNette', width: 130, align: 'right', render: (v) => <span className="bp-amount">{fmt(v)}</span> },
        { title: 'Acc. courtier', dataIndex: 'accessoiresCourtier', key: 'accessoiresCourtier', width: 130, align: 'right', render: (v) => <span className="bp-amount">{fmt(v)}</span> },
        { title: 'Acc. assureur', dataIndex: 'accessoiresAssureur', key: 'accessoiresAssureur', width: 130, align: 'right', render: (v) => <span className="bp-amount">{fmt(v)}</span> },
        { title: 'Taxes', dataIndex: 'taxes', key: 'taxes', width: 110, align: 'right', render: (v) => <span className="bp-amount">{fmt(v)}</span> },
        { title: 'FGA', dataIndex: 'fga', key: 'fga', width: 90, align: 'right', render: (v) => <span className="bp-amount">{fmt(v)}</span> },
        { title: 'CEDEAO', dataIndex: 'cedeao', key: 'cedeao', width: 100, align: 'right', render: (v) => <span className="bp-amount">{fmt(v)}</span> },
        { title: 'Prime TTC', dataIndex: 'primeTTC', key: 'primeTTC', width: 130, align: 'right', render: (v) => <span className="bp-amount is-strong">{fmt(v)}</span> },
        { title: 'Taux comm.', dataIndex: 'tauxCommission', key: 'tauxComm', width: 110, align: 'center', render: fmtPct },
        { title: 'Commission', dataIndex: 'commissionCourtier', key: 'commission', width: 130, align: 'right', render: (v) => <span className="bp-amount is-positive">{fmt(v)}</span> },
        { title: 'Opérateur', dataIndex: 'opérateur', key: 'operateur', width: 150, ellipsis: true },
    ];

    const columns = showOperateur ? allColumns : allColumns.filter(c => c.key !== 'operateur');

    const getAgenceNom = () => resolveAgenceLabel({ selected: selectedAgence, agences, canSwitchAgence, userAgenceId });

    const getCompagnieNom = () => selectedCompagnie !== 'all'
        ? (compagnies.find(c => c._id === selectedCompagnie)?.nom || '')
        : 'TOUTES';

    // Libellé du type d'assurance pour les titres d'export (vide si tous les types)
    const getTypeNom = () => selectedType !== 'all'
        ? (typesAssurance.find(t => t._id === selectedType)?.nom || '')
        : '';

    const getPeriodeLabel = () => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            return { du: dateRange[0].format('DD/MM/YYYY'), au: dateRange[1].format('DD/MM/YYYY') };
        }
        // Sans période choisie, tous les contrats sont affichés
        return null;
    };

    const fmtNum = (v) => (v || 0).toLocaleString('fr-FR').replace(/\u202F/g, ' ').replace(/\u00A0/g, ' ');

    const baseHeaders = ['N°', 'Type assurance', 'N° Attestation', 'N° CEDEAO', 'N° Police', 'Matricule', 'Marque', 'Catégorie', 'Type client', 'Assuré', "Date d'effet", 'Périodes', "Date d'échéance", 'Prime nette', 'Acc. courtier', 'Acc. assureur', 'Taxes', 'FGA', 'CEDEAO', 'Prime TTC', 'Taux comm.', 'Commission'];
    const exportHeaders = showOperateur ? [...baseHeaders, 'Opérateur'] : baseHeaders;

    const exportRows = () => data.map(d => {
        const row = [
            d.numero, d.typeAssurance, d.numeroAttestation, d.numeroCedeao, d.numeroPolice,
            d.immatriculation, d.marqueVehicule, d.categorieVehicule, (d.typeClient || '').toUpperCase(),
            d.assure, fmtDate(d.dateEffet), periodeLabels[d.periode] || d.periode || (d.duree ? `${d.duree} Mois` : ''),
            fmtDate(d.dateEcheance), fmtNum(d.primeNette), fmtNum(d.accessoiresCourtier), fmtNum(d.accessoiresAssureur), fmtNum(d.taxes),
            fmtNum(d.fga), fmtNum(d.cedeao), fmtNum(d.primeTTC), fmtPct(d.tauxCommission), fmtNum(d.commissionCourtier),
        ];
        if (showOperateur) row.push(d.opérateur || '');
        return row;
    });

    const totauxRow = () => {
        const row = [
            { content: '', colSpan: 12, styles: { lineWidth: 0 } },
            { content: 'Total :', styles: { halign: 'right', fontStyle: 'bold' } },
            fmtNum(totaux.primeNette), fmtNum(totaux.accessoiresCourtier), fmtNum(totaux.accessoiresAssureur), fmtNum(totaux.taxes),
            fmtNum(totaux.fga), fmtNum(totaux.cedeao), fmtNum(totaux.primeTTC), { content: '', styles: { lineWidth: 0 } }, fmtNum(totaux.commissionCourtier),
        ];
        if (showOperateur) row.push({ content: '', styles: { lineWidth: 0 } });
        return row;
    };

    const handleExportPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape', format: 'a3' });
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;
        const periode = getPeriodeLabel();
        const agenceNom = getAgenceNom();
        const compagnieNom = getCompagnieNom();
        const typeNom = getTypeNom();

        doc.addImage(logoCap, 'JPEG', 8, 8, 40, 16);
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(today, pageWidth - 8, 15, { align: 'right' });

        doc.setFontSize(12);
        doc.setFont(undefined, 'normal');
        const agenceLabel = 'AGENCE :   ';
        const fullW = doc.getTextWidth(agenceLabel) + doc.getTextWidth(agenceNom);
        const agenceStartX = centerX - fullW / 2;
        doc.text(agenceLabel, agenceStartX, 15);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(139, 0, 0);
        doc.text(agenceNom, agenceStartX + doc.getTextWidth(agenceLabel), 15);

        doc.setFont(undefined, 'bold');
        doc.setFontSize(10);
        doc.setTextColor(0);
        if (periode) {
            const pLabel = 'Période du :';
            const pX = centerX - 60;
            doc.text(pLabel, pX, 24);
            doc.setTextColor(139, 0, 0);
            doc.text(periode.du, pX + doc.getTextWidth(pLabel) + 4, 24);
            doc.setTextColor(0);
            doc.text('Au :', pX + doc.getTextWidth(pLabel) + 4 + doc.getTextWidth(periode.du) + 8, 24);
            doc.setTextColor(139, 0, 0);
            doc.text(periode.au, pX + doc.getTextWidth(pLabel) + 4 + doc.getTextWidth(periode.du) + 8 + doc.getTextWidth('Au :') + 4, 24);
        } else {
            doc.text('Période : toutes', centerX, 24, { align: 'center' });
        }

        const moisAnnee = dateRange ? `${dateRange[0].format('MMMM').toUpperCase()}/${dateRange[0].format('YYYY')}` : '';
        doc.setFontSize(13);
        doc.setTextColor(0, 0, 139);
        doc.setFont(undefined, 'bolditalic');
        doc.text(`BORDEREAU DE PRODUCTION  ${compagnieNom}${typeNom ? `  -  ${typeNom.toUpperCase()}` : ''}   ${moisAnnee}`.trim(), centerX, 33, { align: 'center' });
        doc.setFont(undefined, 'normal');

        const rows = exportRows();
        rows.push(totauxRow());

        autoTable(doc, {
            startY: 38,
            head: [exportHeaders],
            body: rows,
            styles: { fontSize: 6, cellPadding: 2, overflow: 'linebreak', lineColor: [0, 0, 0], lineWidth: 0.3, textColor: [0, 0, 0] },
            headStyles: { fillColor: false, textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 6, cellPadding: 2.5, lineColor: [0, 0, 0], lineWidth: 0.3, halign: 'center' },
            bodyStyles: { fillColor: [255, 255, 255] },
            columnStyles: {
                0: { halign: 'center' },
                13: { halign: 'right' },
                14: { halign: 'right' },
                15: { halign: 'right' },
                16: { halign: 'right' },
                17: { halign: 'right' },
                18: { halign: 'right', fontStyle: 'bold' },
                19: { halign: 'center' },
                20: { halign: 'right', fontStyle: 'bold' },
            },
            margin: { left: 8, right: 8, bottom: 35 },
        });

        const finalY = doc.lastAutoTable.finalY + 8;
        doc.setFontSize(9);
        doc.setTextColor(0);
        doc.setFont(undefined, 'bold');
        doc.text(`Nombre de contrat :   ${data.length}`, 8, finalY);

        const pageCount = doc.internal.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.height;
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(1);
            doc.line(8, pageHeight - 28, pageWidth - 8, pageHeight - 28);
            doc.setFontSize(8);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Cocody 2 Plateaux Vallon ; Rue des Jardins, 28 BP 56 Abidjan 28 / Tel: (+225) 07 10 01 60 54 / 07 88 47 17 79 / 27 22 41 93 50', pageWidth / 2, pageHeight - 20, { align: 'center' });
            doc.setFont(undefined, 'normal');
            doc.setFontSize(7);
            doc.text('RCCM: CI-ABJ-03-2024-B13-023771 | NCC: 2400889R | infos@capassurances.com', pageWidth / 2, pageHeight - 14, { align: 'center' });
        }

        addExporterFooter(doc, exporterName);
        doc.save(`bordereau_production_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté avec succès');
    };

    const handleExportExcel = async () => {
        const today = new Date().toLocaleDateString('fr-FR');
        const periode = getPeriodeLabel();
        const agenceNom = getAgenceNom();
        const compagnieNom = getCompagnieNom();
        const typeNom = getTypeNom();
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Bordereau Production');
        const colWidths = [5, 20, 14, 12, 20, 14, 14, 10, 12, 18, 12, 10, 12, 12, 10, 10, 8, 10, 12, 10, 12];
        if (showOperateur) colWidths.push(16);
        ws.columns = colWidths.map(w => ({ width: w }));

        const logoRes = await fetch(logoCap);
        const logoBlob = await logoRes.blob();
        const logoBuffer = await logoBlob.arrayBuffer();
        const logoId = wb.addImage({ buffer: logoBuffer, extension: 'jpeg' });
        ws.addImage(logoId, { tl: { col: 0, row: 0 }, ext: { width: 160, height: 60 } });
        ws.addRow([]); ws.addRow([]); ws.addRow([]);

        const titleRow = ws.addRow([`BORDEREAU DE PRODUCTION - ${compagnieNom}${typeNom ? ` - ${typeNom.toUpperCase()}` : ''} - AGENCE: ${agenceNom}`]);
        ws.mergeCells(4, 1, 4, exportHeaders.length);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        const dateRow = ws.addRow([`${periode ? `Période du ${periode.du} au ${periode.au}` : 'Période : toutes'} - Exporté le ${today}${exporterName ? `   |   Exporté par : ${exporterName}` : ''}`]);
        ws.mergeCells(5, 1, 5, exportHeaders.length);
        dateRow.getCell(1).font = { italic: true, size: 10, color: { argb: '888888' } };
        dateRow.getCell(1).alignment = { horizontal: 'center' };
        ws.addRow([]);

        const headerRow = ws.addRow(exportHeaders);
        headerRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: '000000' }, size: 9 };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        });

        exportRows().forEach(r => {
            const row = ws.addRow(r);
            row.eachCell((cell, colNumber) => {
                cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
                cell.alignment = { vertical: 'middle', horizontal: BP_MONEY_COLS.has(colNumber) ? 'right' : undefined };
            });
        });

        const totalRow = ws.addRow(totauxRow());
        totalRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E6E6E6' } };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
            if (BP_MONEY_COLS.has(colNumber)) cell.alignment = { vertical: 'middle', horizontal: 'right' };
        });

        const countRow = ws.addRow([`Nombre de contrat : ${data.length}`]);
        countRow.getCell(1).font = { bold: true, size: 10 };

        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `bordereau_production_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté avec succès');
    };

    const exportMenuItems = [
        { key: 'pdf',   label: 'Exporter en PDF',   icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#16a34a' }} />, onClick: handleExportExcel },
    ];

    return (
        <div className="bordereau-production-page">
            {/* Header */}
            <header className="bp-header">
                <div className="bp-header-text">
                    <h1 className="bp-title">
                        <ProfileOutlined /> Bordereau de production
                    </h1>
                    <p className="bp-subtitle">Production par agence et compagnie d'assurance</p>
                </div>
                <div className="bp-header-actions">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        Guide
                    </Button>
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Bordereau de production</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={620}
            >
                <span style={{ color: '#8c8c8c' }}>
                    Ce rapport présente la <strong>production</strong> (contrats souscrits) par
                    agence et par compagnie sur une période donnée.
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <ProfileOutlined style={icStyle} />
                                <div>
                                    <strong>1 · Indicateurs &amp; filtres</strong> — Nombre de
                                    contrats, primes nettes, commissions… Filtrez par
                                    <strong> compagnie</strong>, <strong>agence</strong> et
                                    <strong> période</strong> ; « Effacer » réinitialise.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <FileTextOutlined style={icStyle} />
                                <div>
                                    <strong>2 · Lire le bordereau</strong> — Chaque ligne est un
                                    contrat : prime nette, accessoires courtier, commission, statut
                                    (payé / impayé / versé) et date.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <BankOutlined style={icStyle} />
                                <div>
                                    <strong>3 · Choisir une compagnie</strong> — Sélectionnez une
                                    compagnie et/ou un type d'assurance (ex. Automobile uniquement)
                                    pour produire un bordereau dédié ; ils apparaissent dans le titre
                                    et dans les exports.
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <DownloadOutlined style={icStyle} />
                                <div>
                                    <strong>4 · Exporter</strong> — Le bouton « Exporter » génère un
                                    PDF ou un Excel reprenant la période, l'agence, la compagnie et le type
                                    sélectionnés.
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* KPIs */}
            <section className="bp-kpis" aria-label="Indicateurs bordereau production">
                <MiniKpiCard
                    icon={<FileTextOutlined />}
                    label="Contrats"
                    value={data.length}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<DollarOutlined />}
                    label="Total primes nettes"
                    value={fmt(totaux.primeNette || 0)}
                    tone="indigo"
                />
                <MiniKpiCard
                    icon={<BankOutlined />}
                    label="Total TTC"
                    value={fmt(totaux.primeTTC || 0)}
                    tone="orange"
                />
                <MiniKpiCard
                    icon={<GiftOutlined />}
                    label="Total commissions"
                    value={fmt(totaux.commissionCourtier || 0)}
                    tone="green"
                />
            </section>

            {/* Carte principale */}
            <Card className="bp-main-card">
                <div className="bp-toolbar">
                    {canSwitchAgence && (
                        <Select
                            value={selectedAgence}
                            onChange={setSelectedAgence}
                            className="bp-filter"
                            aria-label="Filtrer par agence"
                            placeholder="Toutes les agences"
                        >
                            <Option value="all">Toutes les agences</Option>
                            {agences.map(a => <Option key={a._id} value={a._id}>{a.nom}</Option>)}
                        </Select>
                    )}
                    <Select
                        value={selectedCompagnie}
                        onChange={setSelectedCompagnie}
                        className="bp-filter"
                        aria-label="Filtrer par compagnie"
                        placeholder="Toutes les compagnies"
                    >
                        <Option value="all">Toutes les compagnies</Option>
                        {compagnies.map(c => <Option key={c._id} value={c._id}>{c.nom}</Option>)}
                    </Select>
                    <Select
                        value={selectedType}
                        onChange={setSelectedType}
                        className="bp-filter"
                        aria-label="Filtrer par type d'assurance"
                        placeholder="Tous les types d'assurance"
                    >
                        <Option value="all">Tous les types d'assurance</Option>
                        {typesAssurance.map(t => <Option key={t._id} value={t._id}>{t.nom}</Option>)}
                    </Select>
                    <RangePicker
                        format="DD/MM/YYYY"
                        onChange={setDateRange}
                        value={dateRange}
                        className="bp-filter-dates"
                        placeholder={['Date début', 'Date fin']}
                        allowClear
                    />
                    {activeFiltersCount > 0 && (
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearAllFilters}
                            className="bp-btn-clear"
                        >
                            Effacer
                        </Button>
                    )}
                    <div className="bp-toolbar-spacer" />
                    <div className="bp-switch">
                        <Switch size="small" checked={showOperateur} onChange={setShowOperateur} />
                        <span className="bp-switch-label">Afficher opérateur</span>
                    </div>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} className="bp-btn-export">
                            Exporter
                        </Button>
                    </Dropdown>
                </div>

                <Table
                    dataSource={data}
                    columns={columns}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="small"
                    scroll={{ x: showOperateur ? 2650 : 2500 }}
                    locale={{ emptyText: loading ? <div style={{ minHeight: 220 }} /> : <Empty description="Aucune donnée pour cette période" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    pagination={{
                        pageSize: 25,
                        showTotal: (t) => `${t} ligne${t > 1 ? 's' : ''}`,
                        showSizeChanger: true,
                        size: 'small',
                    }}
                    summary={() => data.length > 0 ? (
                        <Table.Summary fixed>
                            <Table.Summary.Row className="bp-summary-row">
                                <Table.Summary.Cell index={0} colSpan={13} align="right">
                                    <strong>Total :</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={13} align="right">
                                    <strong className="bp-amount">{fmt(totaux.primeNette)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={14} align="right">
                                    <strong className="bp-amount">{fmt(totaux.accessoiresCourtier)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={15} align="right">
                                    <strong className="bp-amount">{fmt(totaux.accessoiresAssureur)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={16} align="right">
                                    <strong className="bp-amount">{fmt(totaux.taxes)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={17} align="right">
                                    <strong className="bp-amount">{fmt(totaux.fga)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={18} align="right">
                                    <strong className="bp-amount">{fmt(totaux.cedeao)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={19} align="right">
                                    <strong className="bp-amount is-strong">{fmt(totaux.primeTTC)}</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={20} />
                                <Table.Summary.Cell index={21} align="right">
                                    <strong className="bp-amount is-positive">{fmt(totaux.commissionCourtier)}</strong>
                                </Table.Summary.Cell>
                                {showOperateur && <Table.Summary.Cell index={22} />}
                            </Table.Summary.Row>
                        </Table.Summary>
                    ) : null}
                />
            </Card>
        </div>
    );
};

export default BordereauProduction;
